/**
 * SuggestedParlays
 *
 * Reads /api/parlays/{sport}/today and renders:
 *   1. Quality Tiers board (PRIME / STRONG / SOLID) for hits-0.5
 *   2. Suggested 3 / 4 / 5-leg parlays from the quality_tier pool
 *
 * Data source: persisted output of build_parlay.py — see
 * daily_parlay_legs / daily_parlay_suggestions tables.
 */

import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import Sparkline from './Sparkline';
import BarChart from './BarChart';
import SmallMultiplesGrid from './SmallMultiplesGrid';

const TIER_META = {
  PRIME:  { hitRate: 80.6, color: 'text-purple-300', border: 'border-purple-500/50', bg: 'bg-purple-500/10' },
  STRONG: { hitRate: 74.6, color: 'text-blue-300',   border: 'border-blue-500/50',   bg: 'bg-blue-500/10' },
  SOLID:  { hitRate: 68.1, color: 'text-green-300',  border: 'border-green-500/50',  bg: 'bg-green-500/10' },
};

// HR uses a separate model with different historical hit rates than quality tiers.
const HR_TIER_META = {
  LOCK:   { hitRate: 22.3, color: 'text-orange-300', border: 'border-orange-500/50', bg: 'bg-orange-500/10' },
  STRONG: { hitRate: 18.1, color: 'text-yellow-300', border: 'border-yellow-500/50', bg: 'bg-yellow-500/10' },
  SOLID:  { hitRate: 15.1, color: 'text-amber-300',  border: 'border-amber-500/50',  bg: 'bg-amber-500/10' },
};

const STAT_SHORT = {
  hits: 'Hit',
  total_bases: 'TB',
  pitcher_strikeouts: 'K',
  runs: 'R',
  rbis: 'RBI',
  home_runs: 'HR',
};

function fmtOdds(o) {
  if (o === null || o === undefined) return '-';
  return o > 0 ? `+${o}` : String(o);
}

function matchupShort(matchup) {
  if (!matchup) return '';
  for (const sep of [' @ ', ' vs ']) {
    if (matchup.includes(sep)) {
      const [away, home] = matchup.split(sep);
      return `${away.split(' ').slice(-1)[0]}/${home.split(' ').slice(-1)[0]}`;
    }
  }
  return matchup.slice(0, 15);
}

function playerShort(name) {
  if (!name) return '';
  const suffixes = new Set(['Jr.', 'Jr', 'Sr.', 'Sr', 'II', 'III', 'IV']);
  const parts = name.split(' ');
  while (parts.length > 1 && suffixes.has(parts[parts.length - 1])) parts.pop();
  return parts[parts.length - 1] || name;
}

function americanFromDecimal(decimal) {
  if (!decimal || decimal <= 1) return null;
  if (decimal >= 2) return Math.round((decimal - 1) * 100);
  return Math.round(-100 / (decimal - 1));
}

/**
 * Expansion panel — appears as a single full-width row beneath the clicked
 * row. Three cards: L20 bar chart, matchup, season trend / streak.
 * Tufte: every card is data, no decoration.
 */
function ExpandedLegPanel({ leg, points }) {
  if (!leg) return null;
  const line = leg.sb_line;
  const N = points?.length || 0;
  const hits = points?.filter((p) => (p.actual ?? 0) >= line).length || 0;
  const recentPct = N ? (100 * hits) / N : null;

  return (
    <div className="bg-bg-secondary rounded-md p-4 mt-1 mb-2 border border-border/40">
      {/* L20 chart spans full width */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-1">
          <h5 className="text-xs font-semibold uppercase text-text-secondary">
            Last {Math.max(N, 1)} games
          </h5>
          {recentPct !== null && (
            <div className="text-xs text-text-secondary">
              <span className="font-bold text-text-primary">{hits}/{N}</span>
              <span className="ml-1">cleared {line} ({recentPct.toFixed(0)}%)</span>
            </div>
          )}
        </div>
        <BarChart points={points} line={line} height={90} />
      </div>

      {/* Three cards: matchup / season / streak */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
        <div className="bg-bg-primary rounded p-3">
          <div className="text-xs uppercase text-text-secondary mb-1">Matchup</div>
          <div className="text-text-primary font-medium">{leg.matchup || '-'}</div>
          {leg.game_total !== null && leg.game_total !== undefined && (
            <div className="text-xs text-text-secondary mt-1">
              Game total: {Number(leg.game_total).toFixed(1)}
              {leg.book_total !== null && leg.book_total !== undefined && (
                <> &nbsp;·&nbsp; Book: {Number(leg.book_total).toFixed(1)}</>
              )}
            </div>
          )}
          {leg.game_win_prob !== null && leg.game_win_prob !== undefined && (
            <div className="text-xs text-text-secondary">
              Team win prob: {(Number(leg.game_win_prob) * 100).toFixed(0)}%
            </div>
          )}
          {leg.in_lineup === false && (
            <div className="text-xs text-yellow-400 mt-1">⚠ Not confirmed in lineup</div>
          )}
        </div>

        <div className="bg-bg-primary rounded p-3">
          <div className="text-xs uppercase text-text-secondary mb-1">Season trend</div>
          {leg.season_avg !== null && leg.season_avg !== undefined && (
            <div className="text-text-primary">
              Season avg <span className="font-bold">{Number(leg.season_avg).toFixed(2)}</span>
            </div>
          )}
          {leg.rate_per_10ab !== null && leg.rate_per_10ab !== undefined && (
            <div className="text-xs text-text-secondary">
              Rate per 10 AB: {Number(leg.rate_per_10ab).toFixed(2)}
            </div>
          )}
          {leg.recent_rate_per_10ab !== null && leg.recent_rate_per_10ab !== undefined && (
            <div className="text-xs text-text-secondary">
              Recent rate: {Number(leg.recent_rate_per_10ab).toFixed(2)}
              {leg.rate_per_10ab > 0 && (
                <span
                  className={
                    'ml-2 ' +
                    (leg.recent_rate_per_10ab > leg.rate_per_10ab
                      ? 'text-accent-green'
                      : 'text-red-400')
                  }
                >
                  {leg.recent_rate_per_10ab > leg.rate_per_10ab ? '▲' : '▼'}{' '}
                  {(
                    (100 * (leg.recent_rate_per_10ab - leg.rate_per_10ab)) /
                    leg.rate_per_10ab
                  ).toFixed(0)}
                  %
                </span>
              )}
            </div>
          )}
          {leg.consistency_pct !== null && leg.consistency_pct !== undefined && (
            <div className="text-xs text-text-secondary mt-1">
              Consistency (last 10): {Number(leg.consistency_pct).toFixed(0)}%
            </div>
          )}
        </div>

        <div className="bg-bg-primary rounded p-3">
          <div className="text-xs uppercase text-text-secondary mb-1">Streak</div>
          {leg.streak_games_hit && leg.streak_games_window ? (
            <>
              <div className="text-text-primary">
                <span className="font-bold">
                  {leg.streak_games_hit}/{leg.streak_games_window}
                </span>{' '}
                games cleared
              </div>
              {leg.streak_hit_rate !== null && leg.streak_hit_rate !== undefined && (
                <div className="text-xs text-text-secondary">
                  Streak hit rate: {Number(leg.streak_hit_rate).toFixed(0)}%
                </div>
              )}
            </>
          ) : (
            <div className="text-text-secondary text-xs">No active streak.</div>
          )}
          {leg.composite_score !== null && leg.composite_score !== undefined && (
            <div className="text-xs text-text-secondary mt-2 pt-2 border-t border-border/30">
              Composite score:{' '}
              <span className="font-bold text-text-primary">
                {Number(leg.composite_score).toFixed(1)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * CollapsibleSection — a titled block the user can fold away. Keeps the Edges
 * page short by default; secondary sections start collapsed.
 */
function CollapsibleSection({ title, icon, count, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between text-left mb-3 group"
      >
        <h3 className="text-base font-semibold text-text-primary flex items-center gap-2">
          {icon && <span>{icon}</span>}
          {title}
          {count !== undefined && (
            <span className="text-xs text-text-secondary font-normal">({count})</span>
          )}
        </h3>
        <span className="text-xs text-text-secondary group-hover:text-text-primary transition">
          {open ? '▾ hide' : '▸ show'}
        </span>
      </button>
      {open && children}
    </div>
  );
}

export default function SuggestedParlays({ sport = 'mlb' }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Single row open at a time — keeps the eye anchored, avoids long-scroll panels.
  const [expandedId, setExpandedId] = useState(null);
  // Calibration health for inline drift badges; loads in parallel.
  const [tierHealth, setTierHealth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    api.get(`/parlays/${sport}/today`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.detail || 'Failed to load parlays');
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });

    // Parallel fetch — tier health is auxiliary, failure is non-fatal.
    api.get(`/parlays/${sport}/tier-health?days=14`)
      .then((r) => { if (!cancelled) setTierHealth(r.data); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [sport]);

  // expected (frontend constants) + actual (backend) -> a tiny drift badge.
  const healthBadge = (statType, tier, expected) => {
    const cell = tierHealth?.rates?.[statType]?.[tier];
    if (!cell || cell.actual === null || cell.actual === undefined || cell.n < 5) {
      return null;
    }
    const actual = cell.actual;
    const drift = actual - (expected ?? 0);
    const color =
      Math.abs(drift) <= 3
        ? 'text-text-secondary'
        : drift > 0
        ? 'text-accent-green'
        : 'text-red-400';
    const arrow = drift > 1 ? '▲' : drift < -1 ? '▼' : '·';
    return (
      <span className={`text-xs font-normal ml-2 ${color}`}>
        L{tierHealth.window_days}d: {actual.toFixed(0)}% {arrow}{' '}
        {drift > 0 ? '+' : ''}{drift.toFixed(0)}pp
        <span className="text-text-secondary ml-1 opacity-70">(n={cell.n})</span>
      </span>
    );
  };

  // legId -> leg for fast suggestion → leg joining
  const legById = useMemo(() => {
    const m = {};
    if (data?.legs) for (const l of data.legs) m[l.id] = l;
    return m;
  }, [data]);

  // Sparkline lookup helper: leg → { points, line }
  const sparkFor = (leg) => {
    const key = `${leg.player_id}:${leg.stat_type}`;
    const points = data?.sparklines?.[key] || [];
    return { points, line: leg.sb_line };
  };

  const toggleRow = (id) => setExpandedId((cur) => (cur === id ? null : id));

  const tieredLegs = useMemo(() => {
    if (!data?.legs) return { PRIME: [], STRONG: [], SOLID: [] };
    const out = { PRIME: [], STRONG: [], SOLID: [] };
    for (const l of data.legs) if (l.quality_tier && out[l.quality_tier]) out[l.quality_tier].push(l);
    for (const t of Object.keys(out)) {
      out[t].sort((a, b) => (b.tier_ev ?? -99) - (a.tier_ev ?? -99));
    }
    return out;
  }, [data]);

  const qualityParlays = useMemo(() => {
    if (!data?.suggestions) return {};
    const by = {};
    for (const s of data.suggestions) {
      if (s.category !== 'quality_tier') continue;
      by[s.combo_size] = by[s.combo_size] || [];
      by[s.combo_size].push(s);
    }
    return by;
  }, [data]);

  // HR picks: stat_type='home_runs' uses legacy_tier (LOCK/STRONG/SOLID/FADE),
  // not quality_tier — separate model with different historical hit rates.
  const hrPicks = useMemo(() => {
    if (!data?.legs) return { LOCK: [], STRONG: [], SOLID: [] };
    const out = { LOCK: [], STRONG: [], SOLID: [] };
    for (const l of data.legs) {
      if (l.stat_type === 'home_runs' && out[l.legacy_tier]) {
        out[l.legacy_tier].push(l);
      }
    }
    for (const t of Object.keys(out)) {
      out[t].sort((a, b) => (b.prob ?? 0) - (a.prob ?? 0));
    }
    return out;
  }, [data]);

  // Pitcher 2-leg parlays (K + outs combos)
  const pitcherCombos = useMemo(() => {
    if (!data?.suggestions) return [];
    return data.suggestions
      .filter(s => s.category === 'pitcher_strong_outing')
      .sort((a, b) => b.ev - a.ev);
  }, [data]);

  if (isLoading) {
    return (
      <div className="space-y-4 mb-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="glass-card p-6 animate-pulse">
            <div className="h-4 bg-bg-card rounded w-1/3 mb-3"></div>
            <div className="h-12 bg-bg-card rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card bg-red-500/10 border-red-500/30 p-4 mb-6 text-red-400 text-sm">
        {error}
      </div>
    );
  }

  if (!data || data.leg_count === 0) {
    return (
      <div className="glass-card p-6 mb-6 text-center text-text-secondary">
        No parlay board generated for {sport.toUpperCase()} on {data?.game_date || 'today'}.
      </div>
    );
  }

  const hasTiered = ['PRIME', 'STRONG'].some(t => tieredLegs[t].length > 0);
  const hasParlays = Object.keys(qualityParlays).length > 0;
  const hasHR = ['LOCK', 'STRONG'].some(t => hrPicks[t].length > 0);
  const hasPitcherCombos = pitcherCombos.length > 0;

  return (
    <div className="space-y-6 mb-6">
      <div>
        <h2 className="text-lg font-semibold text-text-primary mb-3 flex items-center gap-2">
          <span>&#127921;</span>
          Suggested Parlays ({sport.toUpperCase()})
        </h2>
        <p className="text-xs text-text-secondary">
          Quality tiers + diverse 3/4/5-leg round-robins for {data.game_date}.
          Probabilities use empirical tier hit rates from historical backtests.
        </p>
      </div>

      {/* Small-multiples overview: scan the slate before digging into tables */}
      <SmallMultiplesGrid legs={data.legs || []} sparklines={data.sparklines || {}} />

      {/* Quality Tiers */}
      {hasTiered && (
        <CollapsibleSection title="Quality Tiers — Hits" defaultOpen={true}>
        <div className="space-y-4">
          {['PRIME', 'STRONG'].map(tier => {
            const picks = tieredLegs[tier];
            if (!picks.length) return null;
            const meta = TIER_META[tier];
            return (
              <div key={tier} className={`glass-card p-4 border-l-4 ${meta.border}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`font-bold ${meta.color}`}>
                    {tier} <span className="text-xs text-text-secondary">({meta.hitRate}% historical)</span>
                    {healthBadge('hits', tier, meta.hitRate)}
                  </h3>
                  <span className="text-xs text-text-secondary">{picks.length} pick{picks.length === 1 ? '' : 's'}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-text-secondary uppercase">
                        <th className="pb-2">Player</th>
                        <th className="pb-2">Game</th>
                        <th className="pb-2 text-right">Line</th>
                        <th className="pb-2 text-right">Odds</th>
                        <th className="pb-2 text-center" title="Last 10 games — bar height = stat value, green = hit, gray = miss, dotted line = today's line">L10</th>
                        <th className="pb-2 text-right">Cons</th>
                        <th className="pb-2 text-right">Season</th>
                        <th className="pb-2 text-right">Tier EV</th>
                      </tr>
                    </thead>
                    <tbody>
                      {picks.map(l => {
                        const { points, line } = sparkFor(l);
                        const isOpen = expandedId === l.id;
                        return (
                          <React.Fragment key={l.id}>
                            <tr
                              onClick={() => toggleRow(l.id)}
                              className={`${meta.bg} border-t border-border/30 cursor-pointer hover:brightness-110 transition`}
                            >
                              <td className="py-2 pr-3 text-text-primary font-medium">
                                <span className="text-text-secondary mr-1 inline-block w-3">
                                  {isOpen ? '▾' : '▸'}
                                </span>
                                {l.player_name}
                              </td>
                              <td className="py-2 pr-3 text-text-secondary">{matchupShort(l.matchup)}</td>
                              <td className="py-2 pr-3 text-right">{STAT_SHORT[l.stat_type] || l.stat_type} {l.sb_line}</td>
                              <td className="py-2 pr-3 text-right text-accent-blue">{fmtOdds(l.odds)}</td>
                              <td className="py-2 px-2 text-center">
                                <Sparkline points={points} line={line} />
                              </td>
                              <td className="py-2 pr-3 text-right text-text-secondary">{l.consistency_pct?.toFixed(0) ?? '-'}</td>
                              <td className="py-2 pr-3 text-right text-text-secondary">{l.season_avg?.toFixed(2) ?? '-'}</td>
                              <td className={`py-2 text-right font-medium ${l.tier_ev > 0 ? 'text-accent-green' : 'text-text-secondary'}`}>
                                {l.tier_ev !== null && l.tier_ev !== undefined
                                  ? (l.tier_ev > 0 ? `+${l.tier_ev.toFixed(3)}` : l.tier_ev.toFixed(3))
                                  : '-'}
                              </td>
                            </tr>
                            {isOpen && (
                              <tr>
                                <td colSpan={8} className="p-0">
                                  <ExpandedLegPanel leg={l} points={points} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
        </CollapsibleSection>
      )}

      {/* Suggested Parlays */}
      {hasParlays && (
        <CollapsibleSection title="Round-Robin Parlays (PRIME + STRONG pool)" defaultOpen={false}>
        <div className="space-y-3">
          {[3, 4, 5].map(size => {
            const combos = qualityParlays[size];
            if (!combos || !combos.length) return null;
            return (
              <div key={size} className="glass-card p-4">
                <h4 className="font-semibold text-text-primary mb-3">
                  {size}-Leg Parlays
                  <span className="text-xs text-text-secondary ml-2 font-normal">
                    top {combos.length} by tier-aware EV
                  </span>
                </h4>
                <div className="space-y-2">
                  {combos.map(combo => {
                    const legs = combo.leg_ids.map(id => legById[id]).filter(Boolean);
                    const americanOdds = americanFromDecimal(combo.payout);
                    return (
                      <div key={combo.id} className="bg-bg-secondary rounded-lg p-3">
                        <div className="grid grid-cols-3 gap-2 mb-2 text-center text-xs">
                          <div>
                            <div className="text-text-secondary uppercase">Prob</div>
                            <div className="font-bold text-text-primary">{combo.prob.toFixed(1)}%</div>
                          </div>
                          <div>
                            <div className="text-text-secondary uppercase">Payout</div>
                            <div className="font-bold text-text-primary">
                              {combo.payout.toFixed(2)}x
                              {americanOdds !== null && (
                                <span className="text-accent-blue ml-1">({fmtOdds(americanOdds)})</span>
                              )}
                            </div>
                          </div>
                          <div>
                            <div className="text-text-secondary uppercase">EV</div>
                            <div className={`font-bold ${combo.ev > 0 ? 'text-accent-green' : 'text-red-400'}`}>
                              {combo.ev > 0 ? '+' : ''}{combo.ev.toFixed(3)}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-text-primary flex flex-wrap gap-x-2 gap-y-1">
                          {legs.map((l, i) => (
                            <span key={l.id} className="inline-flex items-center">
                              {i > 0 && <span className="text-text-secondary mr-2">+</span>}
                              <span className={`text-xs font-bold mr-1 ${TIER_META[l.quality_tier]?.color}`}>
                                ({l.quality_tier?.[0]})
                              </span>
                              <span className="font-medium">{playerShort(l.player_name)}</span>
                              <span className="text-text-secondary ml-1">
                                {STAT_SHORT[l.stat_type] || l.stat_type}{l.sb_line}
                              </span>
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
        </CollapsibleSection>
      )}

      {/* HR Picks (separate model — LOCK = 22.3% historical) */}
      {hasHR && (
        <CollapsibleSection title="Home Run Picks" defaultOpen={false}>
        <div className="space-y-4">
          {['LOCK', 'STRONG'].map(tier => {
            const picks = hrPicks[tier];
            if (!picks.length) return null;
            const meta = HR_TIER_META[tier];
            return (
              <div key={`hr-${tier}`} className={`glass-card p-4 border-l-4 ${meta.border}`}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`font-bold ${meta.color}`}>
                    HR {tier} <span className="text-xs text-text-secondary">({meta.hitRate}% historical)</span>
                    {healthBadge('home_runs', tier, meta.hitRate)}
                  </h4>
                  <span className="text-xs text-text-secondary">{picks.length} pick{picks.length === 1 ? '' : 's'}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-text-secondary uppercase">
                        <th className="pb-2">Player</th>
                        <th className="pb-2">Game</th>
                        <th className="pb-2 text-right">cal%</th>
                        <th className="pb-2 text-center" title="Last 10 games — bar = HRs that game">L10 HR</th>
                        <th className="pb-2 text-right">Season HR/10AB</th>
                        <th className="pb-2 text-right">Recent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {picks.map(l => {
                        const { points } = sparkFor(l);
                        const isOpen = expandedId === l.id;
                        return (
                          <React.Fragment key={l.id}>
                            <tr
                              onClick={() => toggleRow(l.id)}
                              className={`${meta.bg} border-t border-border/30 cursor-pointer hover:brightness-110 transition`}
                            >
                              <td className="py-2 pr-3 text-text-primary font-medium">
                                <span className="text-text-secondary mr-1 inline-block w-3">
                                  {isOpen ? '▾' : '▸'}
                                </span>
                                {l.player_name}
                              </td>
                              <td className="py-2 pr-3 text-text-secondary">{matchupShort(l.matchup)}</td>
                              <td className="py-2 pr-3 text-right font-semibold text-text-primary">
                                {l.prob !== null && l.prob !== undefined ? `${l.prob.toFixed(1)}%` : '-'}
                              </td>
                              <td className="py-2 px-2 text-center">
                                <Sparkline points={points} />
                              </td>
                              <td className="py-2 pr-3 text-right text-text-secondary">
                                {l.rate_per_10ab?.toFixed(2) ?? '-'}
                              </td>
                              <td className="py-2 text-right text-text-secondary">
                                {l.recent_rate_per_10ab?.toFixed(2) ?? '-'}
                              </td>
                            </tr>
                            {isOpen && (
                              <tr>
                                <td colSpan={6} className="p-0">
                                  <ExpandedLegPanel leg={l} points={points} />
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
        </CollapsibleSection>
      )}

      {/* Pitcher 2-leg combos (K + outs) */}
      {hasPitcherCombos && (
        <CollapsibleSection title="Pitcher Strong-Outing Parlays" defaultOpen={false}>
        <div className="space-y-3">
          <div className="glass-card p-4">
            <p className="text-xs text-text-secondary mb-3">
              Joint probability assumes independence — K and outs are positively
              correlated, so true EV runs a bit higher than shown.
            </p>
            <div className="space-y-2">
              {pitcherCombos.map(combo => {
                const legs = combo.leg_ids.map(id => legById[id]).filter(Boolean);
                const kLeg = legs.find(l => l.stat_type === 'pitcher_strikeouts');
                const outsLeg = legs.find(l => l.stat_type === 'pitching_outs');
                const americanOdds = americanFromDecimal(combo.payout);
                const pitcherName = kLeg?.player_name || outsLeg?.player_name || 'Unknown';
                return (
                  <div key={combo.id} className="bg-bg-secondary rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-text-primary">{pitcherName}</div>
                      <div className="text-xs text-text-secondary">
                        {matchupShort(kLeg?.matchup || outsLeg?.matchup || '')}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                      {kLeg && (
                        <div className="bg-bg-primary rounded p-2">
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-text-secondary uppercase">K Over</div>
                            <Sparkline points={sparkFor(kLeg).points} line={kLeg.sb_line} width={50} height={14} />
                          </div>
                          <div className="text-text-primary">
                            {kLeg.sb_line} @ <span className="text-accent-blue">{fmtOdds(kLeg.odds)}</span>
                          </div>
                          <div className="text-xs text-text-secondary">
                            {kLeg.prob?.toFixed(1) ?? '-'}% model
                          </div>
                        </div>
                      )}
                      {outsLeg && (
                        <div className="bg-bg-primary rounded p-2">
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-text-secondary uppercase">Outs Over</div>
                            <Sparkline points={sparkFor(outsLeg).points} line={outsLeg.sb_line} width={50} height={14} />
                          </div>
                          <div className="text-text-primary">
                            {outsLeg.sb_line} @ <span className="text-accent-blue">{fmtOdds(outsLeg.odds)}</span>
                          </div>
                          <div className="text-xs text-text-secondary">
                            {outsLeg.prob?.toFixed(1) ?? '-'}% empirical
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div>
                        <div className="text-text-secondary uppercase">Joint Prob</div>
                        <div className="font-bold text-text-primary">{combo.prob.toFixed(1)}%</div>
                      </div>
                      <div>
                        <div className="text-text-secondary uppercase">Payout</div>
                        <div className="font-bold text-text-primary">
                          {combo.payout.toFixed(2)}x
                          {americanOdds !== null && (
                            <span className="text-accent-blue ml-1">({fmtOdds(americanOdds)})</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-text-secondary uppercase">EV</div>
                        <div className={`font-bold ${combo.ev > 0 ? 'text-accent-green' : 'text-red-400'}`}>
                          {combo.ev > 0 ? '+' : ''}{combo.ev.toFixed(3)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        </CollapsibleSection>
      )}
    </div>
  );
}
